/**
 * Who may price whom, as the write side of the API answers it.
 *
 * The authority is `may_write_rate` in `core/permissions/resale.py`: the caller
 * must contain the party whose price the rate sets, which is the seller, or for
 * a provider rate the buying distributor. A platform admin contains everyone.
 * The role floor is MSP admin, so a company sees none of this surface at all.
 *
 * Two things follow, and they are the whole of this module. A distributor or MSP
 * admin *is* the seller, so the seller is derived rather than picked, and only a
 * platform admin has to name it. And the buyer is anyone beneath the seller: not
 * the tier below alone, because the model asks only that the buyer sit inside
 * the seller's tree, and a distributor really does strike direct deals with
 * companies.
 *
 * A distributor entering its own agreement with AWS, Azure or GCP is the third
 * shape, and it is entered by the distributor rather than routed through a
 * platform admin.
 */

import type { OrgScope } from "@/components/scope/OrgTargetPicker";
import type { ResaleRate } from "@/lib/api/resale";
import { Role } from "@/lib/constants/roles";

/** The three shapes a rate row can take, named for the person filling the form. */
export type RateShape =
  /** Seller null: what a distributor buys from the cloud provider for. */
  | "provider"
  /** Buyer null: the seller's default for everything beneath it. */
  | "default"
  /** Both set: one organization's own deal, which replaces the default. */
  | "deal";

const PLATFORM_ROLES: readonly string[] = [
  Role.PLATFORM_ADMIN,
  Role.PLATFORM_MANAGER,
];

/**
 * The tier that reaches a resale surface at all.
 *
 * Companies see prices; only resellers see spread. The API draws the floor in
 * the same place for both surfaces: `may_write_rate` refuses a write beneath
 * MSP admin, and the margin endpoint answers 403 below it. So the list lives
 * once here rather than twice, and a role joining the tier cannot arrive on
 * one surface and not the other.
 */
const RESELLER_TIER_ROLES: readonly string[] = [
  ...PLATFORM_ROLES,
  Role.DISTRIBUTOR_ADMIN,
  Role.MSP_ADMIN,
];

/** True when the role may reach the rate surface at all. */
export function mayManageRates(role: string | undefined): boolean {
  return role != null && RESELLER_TIER_ROLES.includes(role);
}

/**
 * True when the role may read margin.
 *
 * Kept separate from {@link mayManageRates} although both admit the same roles
 * today, because they answer different questions: one is a write of a price,
 * the other a read of the spread that price produces. The API keeps the two
 * apart deliberately, since a buyer may read a rate it may not edit, so a
 * single name covering both would make the next divergence invisible at the
 * call site.
 */
export function mayReadMargin(role: string | undefined): boolean {
  return role != null && RESELLER_TIER_ROLES.includes(role);
}

/**
 * Who is writing, as the write check needs to know it.
 *
 * `homeId` is the root of the caller's own branch: its distributor or MSP, and
 * `null` for platform staff, who are anchored on no organization. `inBranch`
 * answers whether an organization sits inside that branch, for a screen that
 * holds the tree (Organizations lists exactly that branch). It is optional
 * because the ledger holds no tree; see {@link mayWriteRate} for what stands in.
 */
export interface RateActor {
  role: string | undefined;
  homeId: string | null;
  inBranch?: (organizationId: string) => boolean;
}

/**
 * True when the API would accept a write of this rate from this caller.
 *
 * Restates `may_write_rate`: the caller's **own** branch must contain the party
 * whose price the rate sets, which is the seller, or the buying distributor when
 * the seller is the cloud provider. Never the scope bar: a distributor drilled
 * into one of its MSPs is still the seller of that MSP's rate. Controls render
 * only where this is true, so a refusal is the exception rather than the way a
 * reader finds out.
 *
 * With no tree at hand the role answers, and it answers exactly for every rate
 * the caller can read. Platform staff reach every party; a restricted one is
 * narrowed by the server, and by `inBranch` where a screen can pass it. Nothing
 * a distributor reads names a party outside its branch, because the only thing
 * above an organization in it is the distributor itself or the cloud provider.
 * An MSP's branch holds no seller but the MSP.
 */
export function mayWriteRate(
  rate: { seller?: string | null; buyer?: string | null },
  actor: RateActor,
): boolean {
  if (!mayManageRates(actor.role)) return false;
  const target = rate.seller ?? rate.buyer ?? null;
  if (target == null) return false;
  if (actor.homeId != null && target === actor.homeId) return true;
  if (actor.inBranch) return actor.inBranch(target);
  if (actor.role != null && PLATFORM_ROLES.includes(actor.role)) return true;
  return actor.role === Role.DISTRIBUTOR_ADMIN;
}

/**
 * The shapes this role may create.
 *
 * An MSP has no agreement with a cloud provider, so the provider shape is not
 * offered to it. Refusing it here is a convenience, not the control: the server
 * refuses a null seller under any tier but a distributor.
 */
export function shapesFor(role: string | undefined): RateShape[] {
  if (role == null || !mayManageRates(role)) return [];
  if (role === Role.MSP_ADMIN) return ["default", "deal"];
  return ["provider", "default", "deal"];
}

/**
 * True when the form must ask who the seller is.
 *
 * A platform admin is not a party to any rate, so there is nothing to derive
 * the seller from. Everyone else is the seller of what they write.
 */
export function sellerIsPicked(role: string | undefined): boolean {
  return role != null && PLATFORM_ROLES.includes(role);
}

/**
 * Scopes the buyer picker may commit at, deepest last.
 *
 * A seller can only be a distributor or an MSP (the model's `limit_choices_to`),
 * and a buyer is anything beneath one.
 */
export function buyerScopesFor(role: string | undefined): OrgScope[] {
  if (role === Role.MSP_ADMIN) return ["company"];
  if (role === Role.DISTRIBUTOR_ADMIN) return ["msp", "company"];
  if (role != null && PLATFORM_ROLES.includes(role)) {
    return ["distributor", "msp", "company"];
  }
  return [];
}

/** Scopes the seller picker may commit at, for the one role that picks it. */
export function sellerScopes(): OrgScope[] {
  return ["distributor", "msp"];
}

/**
 * The organization this actor sells as, or `null` when there is not one.
 *
 * `null` for a platform admin, who picks it, and for anyone whose token carries
 * no organization at the tier their role claims.
 */
export function sellerIdFor(user: {
  role?: string;
  distributorId?: string;
  mspId?: string;
}): string | null {
  if (user.role === Role.DISTRIBUTOR_ADMIN) return user.distributorId ?? null;
  if (user.role === Role.MSP_ADMIN) return user.mspId ?? null;
  return null;
}

/**
 * The seller and buyer a shape sends, given who is filling the form.
 *
 * The provider shape inverts the usual arrangement: the actor is the *buyer* of
 * that rate, and the seller is the cloud provider, which has no organization row
 * and so is sent as null.
 */
export function partiesFor(
  shape: RateShape,
  actorOrgId: string | null,
  buyerId: string | null,
): { seller: string | null; buyer: string | null } {
  if (shape === "provider") return { seller: null, buyer: actorOrgId };
  if (shape === "default") return { seller: actorOrgId, buyer: null };
  return { seller: actorOrgId, buyer: buyerId };
}

/**
 * The edge in words, from the two names the API already resolved.
 *
 * Each null says something of its own, so neither is rendered as a blank.
 */
export function edgeLabel(
  rate: Pick<ResaleRate, "seller_name" | "buyer_name">,
): string {
  const seller = rate.seller_name ?? "The cloud provider";
  const buyer = rate.buyer_name ?? "everyone beneath";
  return `${seller} → ${buyer}`;
}
